import React from 'react';
import { Button } from '../ui/button';
import { 
    Users, Trophy, Shield, School, Activity, UserX, 
    UserPlus, Clock, ChevronRight, UserCog, Search 
} from 'lucide-react';

const OverviewTab = ({ 
    stats, 
    users, 
    setActiveTab, 
    userFilter, 
    setUserFilter 
}) => {
    if (!stats) return <div className="text-center py-8 text-muted-foreground">Loading stats...</div>;

    return (
        <div className="space-y-6">
            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <Users className="w-6 h-6 text-primary mb-3" />
                    <p className="stat-display text-3xl">{stats?.total_users || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Users</p>
                </div>
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <Activity className="w-6 h-6 text-secondary mb-3" />
                    <p className="stat-display text-3xl">{stats?.total_athletes || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Athletes</p>
                </div>
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <Shield className="w-6 h-6 text-success mb-3" />
                    <p className="stat-display text-3xl">{stats?.verified_athletes || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Verified</p>
                </div>
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <UserX className="w-6 h-6 text-warning mb-3" />
                    <p className="stat-display text-3xl">{stats?.pending_verification || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Pending</p>
                </div>
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <Trophy className="w-6 h-6 text-accent mb-3" />
                    <p className="stat-display text-3xl">{stats?.total_tournaments || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Tournaments</p>
                </div>
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <School className="w-6 h-6 text-purple-500 mb-3" />
                    <p className="stat-display text-3xl">{stats?.total_schools || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Schools</p>
                </div>
            </div>

            {/* Registrations by Category - Prominent Cards */}
            <div>
                <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-primary" />
                    Registrations by Category
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {/* Athletes Card */}
                    <div 
                        className="bg-gradient-to-br from-[#0d2847] to-[#0a1628] border border-cyan-500/30 p-6 cursor-pointer hover:border-cyan-500/60 transition-all group"
                        onClick={() => {
                            setActiveTab('users');
                            setUserFilter({...userFilter, role: 'athlete'});
                        }}
                        data-testid="category-athletes-card"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-cyan-500/20 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-cyan-400" />
                            </div>
                            <span className="text-xs tracking-widest uppercase text-cyan-400">Athletes</span>
                        </div>
                        <p className="stat-display text-4xl text-white group-hover:text-cyan-400 transition-colors">
                            {stats?.users_by_role?.athletes || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                            {stats?.verified_athletes || 0} verified
                        </p>
                    </div>

                    {/* Coaches Card */}
                    <div 
                        className="bg-gradient-to-br from-[#0d2847] to-[#0a1628] border border-green-500/30 p-6 cursor-pointer hover:border-green-500/60 transition-all group"
                        onClick={() => {
                            setActiveTab('users');
                            setUserFilter({...userFilter, role: 'coach'});
                        }}
                        data-testid="category-coaches-card"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                                <UserCog className="w-5 h-5 text-green-400" />
                            </div>
                            <span className="text-xs tracking-widest uppercase text-green-400">Coaches</span>
                        </div>
                        <p className="stat-display text-4xl text-white group-hover:text-green-400 transition-colors">
                            {stats?.users_by_role?.coaches || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">Registered coaches</p>
                    </div>

                    {/* Scouts Card */}
                    <div 
                        className="bg-gradient-to-br from-[#0d2847] to-[#0a1628] border border-orange-500/30 p-6 cursor-pointer hover:border-orange-500/60 transition-all group"
                        onClick={() => {
                            setActiveTab('users');
                            setUserFilter({...userFilter, role: 'scout'});
                        }}
                        data-testid="category-scouts-card"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-orange-500/20 flex items-center justify-center">
                                <Search className="w-5 h-5 text-orange-400" />
                            </div>
                            <span className="text-xs tracking-widest uppercase text-orange-400">Scouts</span>
                        </div>
                        <p className="stat-display text-4xl text-white group-hover:text-orange-400 transition-colors">
                            {stats?.users_by_role?.scouts || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">Talent scouts</p>
                    </div>

                    {/* School Admins Card */}
                    <div 
                        className="bg-gradient-to-br from-[#0d2847] to-[#0a1628] border border-purple-500/30 p-6 cursor-pointer hover:border-purple-500/60 transition-all group"
                        onClick={() => {
                            setActiveTab('users');
                            setUserFilter({...userFilter, role: 'school_admin'});
                        }}
                        data-testid="category-school-admins-card"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
                                <School className="w-5 h-5 text-purple-400" />
                            </div>
                            <span className="text-xs tracking-widest uppercase text-purple-400">School Admins</span>
                        </div>
                        <p className="stat-display text-4xl text-white group-hover:text-purple-400 transition-colors">
                            {stats?.users_by_role?.school_admins || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">School administrators</p>
                    </div>

                    {/* Organizers Card */}
                    <div 
                        className="bg-gradient-to-br from-[#0d2847] to-[#0a1628] border border-yellow-500/30 p-6 cursor-pointer hover:border-yellow-500/60 transition-all group"
                        onClick={() => {
                            setActiveTab('users');
                            setUserFilter({...userFilter, role: 'organizer'});
                        }}
                        data-testid="category-organizers-card"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center">
                                <Trophy className="w-5 h-5 text-yellow-400" />
                            </div>
                            <span className="text-xs tracking-widest uppercase text-yellow-400">Organizers</span>
                        </div>
                        <p className="stat-display text-4xl text-white group-hover:text-yellow-400 transition-colors">
                            {stats?.users_by_role?.organizers || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">Tournament organizers</p>
                    </div>
                </div>
            </div>

            {/* Recent Registrations and Sports Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Registrations */}
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        Recent Registrations
                    </h3>
                    <div className="space-y-3 max-h-72 overflow-y-auto">
                        {users.slice(0, 10).map((user) => (
                            <div key={user.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold
                                        ${user.role === 'athlete' ? 'bg-cyan-500/20 text-cyan-400' : 
                                          user.role === 'coach' ? 'bg-green-500/20 text-green-400' : 
                                          user.role === 'scout' ? 'bg-orange-500/20 text-orange-400' : 
                                          user.role === 'school_admin' ? 'bg-purple-500/20 text-purple-400' : 
                                          'bg-yellow-500/20 text-yellow-400'}`}>
                                        {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">{user.full_name}</p>
                                        <p className="text-xs text-muted-foreground">{user.email}</p>
                                    </div>
                                </div>
                                <span className={`px-2 py-1 text-xs uppercase
                                    ${user.role === 'athlete' ? 'bg-cyan-500/20 text-cyan-400' : 
                                      user.role === 'coach' ? 'bg-green-500/20 text-green-400' : 
                                      user.role === 'scout' ? 'bg-orange-500/20 text-orange-400' : 
                                      user.role === 'school_admin' ? 'bg-purple-500/20 text-purple-400' : 
                                      user.role === 'organizer' ? 'bg-yellow-500/20 text-yellow-400' :
                                      'bg-primary/20 text-primary'}`}>
                                    {user.role}
                                </span>
                            </div>
                        ))}
                        {users.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">No registrations yet</p>
                        )}
                    </div>
                    {users.length > 10 && (
                        <Button 
                            variant="ghost" 
                            className="w-full mt-4 text-primary hover:text-primary hover:bg-primary/10"
                            onClick={() => setActiveTab('users')}
                        >
                            View All Users
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    )}
                </div>
                
                {/* Athletes by Sport */}
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h3 className="font-heading font-bold text-lg uppercase mb-4">Athletes by Sport</h3>
                    <div className="space-y-3 max-h-72 overflow-y-auto">
                        {stats?.sports_breakdown?.filter(s => s.athletes > 0).map((item) => (
                            <div key={item.sport} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                <span className="text-muted-foreground text-sm">{item.sport}</span>
                                <span className="stat-display text-xl">{item.athletes}</span>
                            </div>
                        ))}
                        {(!stats?.sports_breakdown || stats.sports_breakdown.filter(s => s.athletes > 0).length === 0) && (
                            <p className="text-sm text-muted-foreground text-center py-4">No athletes registered yet</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverviewTab;
